// @ts-nocheck — composed component; types added when touched again later
import React from "react";
// Phase 4 sub-step 10b — explicit primitive imports. Card/Button are also
// published on window for back-compat with the legacy bare-name pattern;
// new modules should prefer this ESM import.
import { Card, Button } from "../../legacy/primitives";
// DealInspector — click a Real value → popover lists the contributing HubSpot
// deals. Built after the €1.014-in-June ghost-deal episode where we couldn't
// identify which deal was driving a given cell value.
import DealInspector from "./DealInspector";
// Tooltip — hover-only explainer; used on the GTM Real cell so the displayed
// total stays a single clean number while the sales + team-Upsell split
// remains discoverable on hover.
import Tooltip from "../ui/Tooltip";

/* composed/ForecastTable.tsx — primary edit surface (Phase 4 sub-step 4).
   Relocated from src/legacy/forecast-table.tsx. Still reads from window for
   SectionHeader / EditableNumber / useCanEdit (defined in legacy app.tsx);
   Card and Button now arrive via ESM (sub-step 10b). Behavior preserved
   byte-for-byte. */

// === Legacy file header preserved ===
// forecast-table.jsx — the dashboard's primary edit surface, extracted from
// app.jsx as Phase B sub-PR 3 (the last and largest sub-PR of the module split).
//
// What lives here:
//   - <ForecastTable>     — top-level wrapper (Card + SectionHeader + body)
//   - <MonthRow>          — one row per month in the FY/YTD table view
//   - <MonthDetailCard>   — focused single-month / per-quarter card view
//   - <OthersCell>        — two-row Others cell with click-to-edit forecast
//   - <BoundChannelCell>  — Partner / Self-Serve cell helper (€ + % + actual)
//   - <MixBar>            — channel mix horizontal bar in the Mix column
//   - <MomBadge>          — MoM % delta badge for tableShowMoM mode
//   - <EditableText>      — inline-edit text helper (used in <thead> for AE name/person)
//
// Public API:
//   <ForecastTable
//     state setState t lang rows visibleRows today actuals timeframe
//     mrrFmt tableEur
//     tableValueMode setTableValueMode
//     tableExpanded setTableExpanded
//     sectionOpen onToggleSection
//   />
//
// Loading: <script src="forecast-table.jsx"> in Forecast.html, AFTER
// primitives.jsx (uses Card, SectionHeader, Button, EditableNumber via window)
// and BEFORE app.jsx. Components reference EditableNumber via window because
// it lives in app.jsx (still used in 18 places outside the table).

const { useEscapeClose: _useEscapeClose_ft, useOutsideClick: _useOutsideClick_ft } = window;

// ─── Inline-edit text helper (AE name + person) ────────────────────────────
function EditableText({ value, onChange, className = "", kind = null }) {
  const useCanEdit = window.useCanEdit;
  const [editing, setEditing] = React.useState(false);
  const [tmp, setTmp] = React.useState(value || "");
  const canEdit = useCanEdit(kind, null);
  React.useEffect(() => { setTmp(value || ""); }, [value]);
  if (editing) {
    return (
      <input autoFocus type="text" className={`edit-input ${className}`}
        value={tmp} onChange={(e) => setTmp(e.target.value)}
        onBlur={() => { onChange(tmp); setEditing(false); }}
        onKeyDown={(e) => { if (e.key === "Enter") { onChange(tmp); setEditing(false); } if (e.key === "Escape") setEditing(false); }}
        style={{ width: Math.max(70, (tmp.length + 1) * 9) + "px" }} />
    );
  }
  return <span className={`editable ${className} ${!canEdit ? "editable--locked" : ""}`}
    onClick={() => { if (canEdit) setEditing(true); }}
    title={canEdit ? "Click to edit" : "Modifica bloccata — clicca 🔒 in alto"}>{value || "—"}</span>;
}

// ─── MixBar — channel mix horizontal bar ───────────────────────────────────
function MixBar({ row }) {
  const segs = [
    {k:"gtm",     v: row.gtmPct,         c:"var(--lilac-700)"},
    {k:"ae",      v: row.aePct,          c:"var(--ae-500)"},
    {k:"partner", v: row.partnerPct,     c:"var(--lilac-400)"},
    {k:"self",    v: row.selfServePct,   c:"var(--lilac-300)"},
    {k:"others",  v: row.othersPct,      c:"var(--lilac-200)"},
  ];
  return (
    <div className="mixbar" title={`GTM ${segs[0].v.toFixed(0)}% · AE ${segs[1].v.toFixed(0)}% · Partner ${segs[2].v.toFixed(0)}% · Self ${segs[3].v.toFixed(0)}% · Others ${segs[4].v.toFixed(0)}%`}>
      {segs.map(s => s.v > 0 && (<div key={s.k} className="mixbar__seg" style={{width: Math.max(0, s.v) + "%", background: s.c}} />))}
    </div>
  );
}

// ─── MomBadge — MoM % delta when tableShowMoM is on ────────────────────────
// Helper to render a MoM % delta badge next to a numeric value.
function MomBadge({ cur, prev, show }) {
  if (!show) return null;
  if (typeof cur !== "number" || cur == null || isNaN(cur)) return null;
  if (typeof prev !== "number" || !prev) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  if (Math.abs(pct) < 0.5) return <span className="mom-delta mom-delta--flat">~</span>;
  const tone = pct > 0 ? "up" : "down";
  return <span className={`mom-delta mom-delta--${tone}`}>{pct > 0 ? "+" : ""}{Math.round(pct)}%</span>;
}

// ─── BoundChannelCell — Partner / Self-Serve cell helper ───────────────────
// € is the input. % is derived from targetAdd (read-only display).
// Optional deals/portalId/ownersMap/cellLabel — when provided, a DealInspector
// trigger is rendered next to the actual value so the user can drill into the
// HubSpot deals that sum to it (added after the June ghost-deal episode).
function BoundChannelCell({ eur, pct, actual, onEur, onActual, lang, deals, portalId, ownersMap, fmtMoney, cellLabel }) {
  const EditableNumber = window.EditableNumber;
  const fmt = fmtMoney || window.eur;
  return (
    <div className="cell-stack">
      <div className="cell-row v-primary">
        <EditableNumber value={Math.round(eur)} onChange={onEur} prefix="€" />
        <span className="v-secondary" style={{fontSize: "var(--fs-caption)"}}>{pct.toFixed(0)}%</span>
      </div>
      <div className="cell-row v-secondary">
        <span className="v-label">R</span>
        <EditableNumber value={actual} onChange={onActual} prefix="€" allowEmpty={true} placeholder="—" />
        {Array.isArray(deals) && deals.length > 0 && (
          <DealInspector
            deals={deals}
            portalId={portalId || null}
            ownersMap={ownersMap}
            lang={lang}
            fmtMoney={fmt}
            cellLabel={cellLabel}
          />
        )}
        {actual != null && eur > 0 && (
          <span className={`perf-mini ${actual >= eur*0.95 ? "good" : actual >= eur*0.7 ? "ok" : "bad"}`}>
            {Math.round((actual / eur) * 100)}%
          </span>
        )}
      </div>
    </div>
  );
}

// ─── OthersCell — two-row Others cell ──────────────────────────────────────
// Row 1 (primary): forecast TOTAL. Click flips it into edit mode, where the same row
//   exposes the two underlying forecast inputs (Setup + Upsell) inline. Click outside
//   or press Escape to exit edit mode.
// Row 2 (secondary): HubSpot REAL chips — Setup actual + Upsell actual + Other AEs.
//   Each chip is hidden when 0 (so future months render an empty "—"); all chips share
//   the same font with the row, which is how the rest of the table behaves.
function OthersCell({ row, m, setupFcst, upsellFcst, setupReal, otherUpsellReal, otherReal, otherDeals, setupDeals, otherUpsellDeals, portalId, ownersMap, eur, setMonth, lang, othersTotal, tip }) {
  const EditableNumber = window.EditableNumber;
  const [editing, setEditing] = React.useState(false);
  const cellRef = React.useRef(null);
  // Shared hooks — same dismiss behavior as every other floating surface.
  const exitEdit = React.useCallback(() => setEditing(false), []);
  _useEscapeClose_ft(editing, exitEdit);
  _useOutsideClick_ft(cellRef, exitEdit, editing);

  // Build real chips, dropping zeros so future months show "—" instead of "€0+€0+€0".
  // Each chip carries its deals list so the inspector popover can drill into HubSpot.
  //
  // 2026-05 — per Marco's directive, the team's Upsell chip moves to GTM
  // (rolled into the GTM cell total). The Others column keeps only what's
  // legitimately "not GTM team's revenue":
  //   - Setup       — one-off fees, never part of channel MRR
  //   - Other AEs   — country=Italy GTM deals owned outside the team
  //   - Other Upsell — country=Italy upsells owned outside the team
  const realChips = [];
  if (setupReal > 0)        realChips.push({ key: "setup",       label: lang === "it" ? "Setup"        : "Setup",        val: setupReal,        deals: setupDeals       || [] });
  if (otherReal > 0)        realChips.push({ key: "otherGtm",    label: lang === "it" ? "Altri AE"     : "Other AEs",    val: otherReal,        deals: otherDeals       || [] });
  if (otherUpsellReal > 0)  realChips.push({ key: "otherUpsell", label: lang === "it" ? "Altri Upsell" : "Other Upsell", val: otherUpsellReal,  deals: otherUpsellDeals || [] });

  return (
    <div className="cell-stack others-cell" ref={cellRef} title={tip}>
      <div className="cell-row v-primary others-total-row">
        {editing ? (
          // Edit mode: same row, two inline inputs for the underlying forecast values.
          // Sum is implicit (top stays consistent on save). 'Setup + Upsell' phrasing.
          <span className="others-fcst-edit">
            <EditableNumber value={setupFcst} onChange={(v) => setMonth(row.key, {setupUpsell: v})} prefix="€" />
            <span className="others-pair__sep">+</span>
            <EditableNumber value={upsellFcst} onChange={(v) => setMonth(row.key, {upsellPipeline: v})} prefix="€" />
          </span>
        ) : (
          <span
            className="others-total others-total--clickable"
            onClick={() => setEditing(true)}
            title={lang === "it" ? "Click per modificare il forecast" : "Click to edit forecast"}
          >
            {eur(othersTotal)}
          </span>
        )}
      </div>
      <div className="cell-row v-secondary others-real-row">
        {realChips.length === 0 ? (
          <span className="others-real__empty">—</span>
        ) : realChips.map((c, i) => (
          <React.Fragment key={c.key}>
            {i > 0 && <span className="others-pair__sep">+</span>}
            <span className="others-real" title={`${c.label} (HubSpot): ${eur(c.val)}`}>€{c.val}</span>
            {c.deals.length > 0 && (
              <DealInspector
                deals={c.deals}
                portalId={portalId || null}
                ownersMap={ownersMap}
                lang={lang}
                fmtMoney={eur}
                cellLabel={c.label}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── MonthDetailCard — focused single-month / per-quarter view ─────────────
// Phase D: takes state slices (monthData, aes) instead of `state` so React.memo
// can shallow-compare effectively. Re-renders only when this row's slice or aes
// actually change, not on every Dashboard state change.
function MonthDetailCard({ row, monthData, aes, setMonth, setAeMonth, lang, isCurrent, mrrFmt, tableEur }) {
  const EditableNumber = window.EditableNumber;
  if (!row) return null;
  const m = monthData || {};
  const eur = mrrFmt || window.eur;
  const label = lang === "it" ? window.MONTH_LABELS_IT[row.key] : window.MONTH_LABELS_EN[row.key];
  const aeHired = (aes || []).length;
  const aeNeeded = row.aeHeadcountNeeded || 0;
  const aeGap = Math.max(0, Math.round(aeNeeded) - aeHired);
  const realPct = row.eomTarget > 0 && row.realEom != null ? (row.realEom / row.eomTarget) * 100 : null;
  const tone = realPct == null ? "neutral" : realPct >= 97 ? "good" : realPct >= 85 ? "ok" : "bad";

  const channels = [
    { k: "gtm",     label: "GTM",       eur: row.gtmTarget,    actual: row.gtmActual,    color: "var(--lilac-700)", editable: true,
      onTarget: (v) => setMonth(row.key, {gtmTarget: v}),  onActual: (v) => setMonth(row.key, {gtmActual: v}) },
    { k: "ae",      label: "AE",        eur: row.aeChannelGap, actual: null,             color: "var(--ae-500)", computed: true },
    { k: "partner", label: "Partner",   eur: row.partnerMrr,   actual: row.partnerActual,color: "var(--lilac-400)", editable: true,
      onTarget: (v) => setMonth(row.key, {partnerMrr: v}),  onActual: (v) => setMonth(row.key, {partnerActual: v}) },
    { k: "self",    label: "Self-Serve",eur: row.selfServeMrr, actual: row.selfServeActual,color: "var(--lilac-300)", editable: true,
      onTarget: (v) => setMonth(row.key, {selfServeMrr: v}),onActual: (v) => setMonth(row.key, {selfServeActual: v}) },
    { k: "others",  label: "Others",    eur: row.othersTotal,  actual: null, color: "var(--lilac-200)", computed: true, na: true },
  ];

  return (
    <div className={`mdc ${isCurrent ? "mdc--current" : ""} mdc--${tone}`}>
      <div className="mdc__head">
        <div className="mdc__title">
          <h2>{label}</h2>
          {isCurrent && <span className="mdc__pill">{lang === "it" ? "MESE CORRENTE" : "CURRENT MONTH"}</span>}
          {row.locked && <span className="mdc__pill mdc__pill--lock">🔒 {lang === "it" ? "CHIUSO" : "LOCKED"}</span>}
        </div>
        <div className="mdc__seas">
          {lang === "it" ? "Stagionalità" : "Seasonality"}:&nbsp;
          <EditableNumber value={m.seasonality ?? 100} onChange={(v) => setMonth(row.key, {seasonality: v})} suffix="%" />
        </div>
      </div>

      <div className="mdc__hero">
        <div className="mdc__hero-cell">
          <div className="mdc__cell-label">{lang === "it" ? "EOM Target" : "EOM Target"}</div>
          <div className="mdc__cell-value">
            <EditableNumber value={Math.round(row.eomTarget)} onChange={(v) => setMonth(row.key, {eomTarget: v})} prefix="€" displayFormatter={(v) => tableEur(v)} />
          </div>
        </div>
        <div className="mdc__hero-cell">
          <div className="mdc__cell-label">{lang === "it" ? "EOM Real" : "EOM Real"}</div>
          <div className="mdc__cell-value">
            {row.realEom != null && (row.locked || row.hasAnyActuals) ? tableEur(row.realEom) : <span className="mdc__na">—</span>}
            {realPct != null && (
              <span className={`mdc__delta-pill mdc__delta-pill--${tone}`}>{realPct >= 100 ? "+" : ""}{(realPct - 100).toFixed(0)}%</span>
            )}
          </div>
        </div>
        <div className="mdc__hero-cell">
          <div className="mdc__cell-label">{lang === "it" ? "Add MRR Target" : "Add MRR Target"}</div>
          <div className="mdc__cell-value">{tableEur(row.targetAdd)}</div>
          <div className="mdc__cell-sub">R: {row.hasAnyActuals ? eur(row.realAdd) : "—"}</div>
        </div>
        <div className="mdc__hero-cell">
          <div className="mdc__cell-label">Churn</div>
          <div className="mdc__cell-value">
            <EditableNumber value={Math.round(row.churnAbs)} onChange={(v) => setMonth(row.key, {churnAbs: v})} prefix="€" />
          </div>
          <div className="mdc__cell-sub">{row.churnPct.toFixed(1)}% {lang === "it" ? "del MRR" : "of MRR"}</div>
        </div>
      </div>

      <div className="mdc__channels">
        <div className="mdc__channels-head">
          <span>{lang === "it" ? "Canali (target → real)" : "Channels (target → real)"}</span>
        </div>
        <div className="mdc__channel-list">
          {channels.map(c => {
            const perfPct = (c.actual != null && c.eur > 0) ? (c.actual / c.eur) * 100 : null;
            return (
              <div key={c.k} className="mdc__ch">
                <div className="mdc__ch-bar" style={{background: c.color}} />
                <div className="mdc__ch-name">{c.label}</div>
                <div className="mdc__ch-target">
                  {c.na
                    ? <span className="mdc__na" style={{fontSize: "var(--fs-body-sm)"}}>N/A</span>
                    : c.editable
                      ? <EditableNumber value={Math.round(c.eur)} onChange={c.onTarget} prefix="€" />
                      : <span>{eur(c.eur)}</span>}
                </div>
                <div className="mdc__ch-arrow">→</div>
                <div className="mdc__ch-real">
                  {c.editable
                    ? <EditableNumber value={c.actual} onChange={c.onActual} prefix="€" allowEmpty={true} placeholder="—" />
                    : c.k === "others"
                      ? <span>{eur(row.othersTotal)}</span>
                      : <span className="mdc__na">—</span>}
                  {perfPct != null && (
                    <span className={`perf-mini ${perfPct >= 95 ? "good" : perfPct >= 70 ? "ok" : "bad"}`}>{Math.round(perfPct)}%</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mdc__footer">
        <div className="mdc__footer-row">
          <span className="mdc__footer-label">{lang === "it" ? "AE necessari" : "AEs needed"}:</span>
          <span className={`ae-need-pill ${aeGap > 0 ? "ae-need-pill--gap" : "ae-need-pill--ok"}`}>
            <span className="ae-need-pill__num"><b>{aeHired}</b>/<b>{Math.round(aeNeeded)}</b></span>
            {aeGap > 0
              ? <span className="ae-need-label">+{aeGap} {lang === "it" ? "da assumere" : "to hire"}</span>
              : <span className="ae-need-label">{lang === "it" ? "team ok" : "team ok"}</span>}
          </span>
        </div>
        {row.aes && row.aes.length > 0 && (
          <div className="mdc__ae-grid">
            {row.aes.map(ae => {
              const mAE = (m.aes || {})[ae.id] || {};
              const real = (typeof mAE.real === "number") ? mAE.real : null;
              const pct = (real != null && ae.target > 0) ? (real / ae.target) * 100 : null;
              return (
                <div key={ae.id} className="mdc__ae">
                  <div className="mdc__ae-name">{ae.name}{ae.person ? ` · ${ae.person}` : ""}</div>
                  <div className="mdc__ae-numbers">
                    <span className="mdc__ae-target">
                      <EditableNumber
                        value={ae.target}
                        onChange={(v) => setAeMonth(row.key, ae.id, {target: v})}
                        prefix="€"
                        kind="ae" monthKey={row.key}
                      />
                    </span>
                    <span className="mdc__ch-arrow">→</span>
                    <EditableNumber value={real} onChange={(v) => setAeMonth(row.key, ae.id, {real: v})} prefix="€" allowEmpty={true} placeholder="—" kind="ae" monthKey={row.key} />
                    {pct != null && <span className={`perf-mini ${pct >= 95 ? "good" : pct >= 70 ? "ok" : "bad"}`}>{Math.round(pct)}%</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {m.note && <div className="mdc__note">📝 {m.note}</div>}
      </div>
    </div>
  );
}
// Phase D: memoize MonthDetailCard. Default shallow comparison is correct
// because every prop is now a primitive or stable reference (monthData and
// aes change only when the row's slice or AE list actually changes).
const MonthDetailCardMemo = React.memo(MonthDetailCard);

// ─── MonthRow — one row per month in the FY/YTD table view ─────────────────
// Phase D: takes state slices (monthData, lang, aes, tableShowMoM) instead of
// the whole `state` object. Combined with React.memo (below), this means
// MonthRow only re-renders when its specific row's data, the language,
// the AE list, or the MoM toggle actually changes — not on every Dashboard
// state change. State setters (setState-derived) come in via useCallback
// from <ForecastTable> so their refs are stable across renders.
function MonthRow({ r, monthData, lang, aes, tableShowMoM, setMonth, setAeMonth, t, isCurrent, prevExtraHires, prevRow, mrrFmt, tableEur, otherDeals, ownersMap, dealsByCell, realAddDeals, portalId }) {
  const EditableNumber = window.EditableNumber;
  const m = monthData;
  const label = lang === "it" ? window.MONTH_LABELS_IT[r.key] : window.MONTH_LABELS_EN[r.key];
  // INSIDE THE TABLE: default formatter is MRR (overrides global ARR effect).
  // Use `tableEur` only on cells that should respond to the table-local toggle (TOT MRR EOM, Target columns).
  const eur = mrrFmt || window.eur;
  const aeHired = (aes || []).length;
  const aeNeeded = r.aeHeadcountNeeded || 0;
  const aeGap = Math.max(0, Math.round(aeNeeded) - aeHired);
  const isHireMonth = !r.locked && (r.extraHiresNeeded || 0) > (prevExtraHires || 0);
  const realEomShown = (r.locked || r.realAdd > 0 || r.gtmActual != null || (r.partnerActual != null) || (r.selfServeActual != null) || (r.aes || []).some(a => {
    const mAE = (m.aes || {})[a.id] || {};
    return typeof mAE.real === "number";
  }));

  return (
    <tr className={`${r.locked ? "row--lock" : "row--proj"} ${isCurrent ? "row--current" : ""} ${isHireMonth ? "row--hire" : ""}`}>
      <td>
        <div className="month">
          <span className={`dot ${r.locked ? "dot--lock" : "dot--proj"}`} />
          <div className="month__name">
            {isCurrent && <span aria-hidden="true" style={{marginRight: 4, fontSize: "0.9em", color: "var(--lilac-700)", verticalAlign: "middle"}}>⊙</span>}
            {label}
            {isCurrent && <span className="now-tag">{lang === "it" ? "ora" : "now"}</span>}
            {isHireMonth && <span className="hire-tag" title={lang === "it" ? "Nuovo AE da assumere" : "New AE to hire"}>+{(r.extraHiresNeeded || 0) - (prevExtraHires || 0)} hire</span>}
          </div>
        </div>
      </td>
      <td className="num compact-hide">
        <EditableNumber value={m.seasonality ?? 100} onChange={(v) => setMonth(r.key, {seasonality: v})} suffix="%" />
      </td>

      {/* EOM target (EDITABLE) / real (computed) — responds to table-local ARR toggle */}
      <td className="num">
        <div className="cell-stack">
          <div className="cell-row v-primary">
            <EditableNumber value={Math.round(r.eomTarget)} onChange={(v) => setMonth(r.key, {eomTarget: v})} prefix="€" displayFormatter={(v) => tableEur(v)} />
            <MomBadge cur={r.eomTarget} prev={prevRow?.eomTarget} show={tableShowMoM} />
          </div>
          <div className="cell-row v-secondary">
            <span className="v-label">R</span>
            <span className="v-actual">{realEomShown ? tableEur(r.realEom) : "—"}</span>
            {realEomShown && (
              <MomBadge cur={r.realEom} prev={prevRow?.realEom} show={tableShowMoM} />
            )}
            {realEomShown && r.eomTarget > 0 && (
              <span className={`perf-mini ${r.realEom >= r.eomTarget*0.95 ? "good" : r.realEom >= r.eomTarget*0.7 ? "ok" : "bad"}`}>
                {Math.round((r.realEom / r.eomTarget) * 100)}%
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Target Add (derived from EOM) / Real Add (sum of actuals) — Target Add follows table toggle */}
      <td className="num">
        <div className="cell-stack">
          <div className="cell-row v-primary">
            <span>{tableEur(r.targetAdd)}</span>
          </div>
          <div className="cell-row v-secondary">
            <span className="v-label">R</span>
            <span className="v-actual">{r.realAdd > 0 ? eur(r.realAdd) : "—"}</span>
            <DealInspector
              deals={realAddDeals || []}
              portalId={portalId}
              ownersMap={ownersMap}
              lang={lang}
              fmtMoney={eur}
              cellLabel={lang === "it" ? "Real Add" : "Real Add"}
              total={r.realAdd}
              breakdown={[
                // 2026-05 reshuffle: team Upsell is now part of "GTM" in the
                // user's mental model, so it's grouped under the GTM row in
                // the breakdown too. The deals list below still surfaces them
                // individually if the user wants to inspect them one-by-one.
                { label: lang === "it" ? "GTM (HubSpot, incl. Upsell team)" : "GTM (HubSpot, incl. team Upsell)",
                  amount: (dealsByCell?.gtmActual || []).reduce((s, d) => s + (d.amount || 0), 0)
                        + (dealsByCell?.upsellPipeline || []).reduce((s, d) => s + (d.amount || 0), 0),
                  dealCount: (dealsByCell?.gtmActual || []).length + (dealsByCell?.upsellPipeline || []).length },
                { label: lang === "it" ? "Partner (HubSpot)"     : "Partner (HubSpot)",     amount: (dealsByCell?.partnerActual     || []).reduce((s, d) => s + (d.amount || 0), 0), dealCount: (dealsByCell?.partnerActual     || []).length },
                { label: lang === "it" ? "Self-Serve (HubSpot)"  : "Self-Serve (HubSpot)",  amount: (dealsByCell?.selfServeActual   || []).reduce((s, d) => s + (d.amount || 0), 0), dealCount: (dealsByCell?.selfServeActual   || []).length },
                { label: lang === "it" ? "Altri AE (HubSpot)"    : "Other AEs (HubSpot)",   amount: (dealsByCell?.otherActual       || []).reduce((s, d) => s + (d.amount || 0), 0), dealCount: (dealsByCell?.otherActual       || []).length },
                { label: lang === "it" ? "Altri Upsell (HubSpot)": "Other Upsell (HubSpot)", amount: (dealsByCell?.otherUpsellActual || []).reduce((s, d) => s + (d.amount || 0), 0), dealCount: (dealsByCell?.otherUpsellActual || []).length },
                // Manual per-AE entries — not HubSpot deals, can't be drilled
                // into. Shows up only when an AE has typed a real value for
                // this month in the per-AE columns. Surfaces the cause of any
                // delta between Real Add and Contratti's Won MRR.
                { label: lang === "it" ? "Per-AE (manuale)"      : "Per-AE (manual)",       amount: r.aeTotalReal || 0, manual: true },
              ]}
            />
            {r.targetAdd > 0 && r.realAdd > 0 && (
              <span className={`perf-mini ${r.realAdd >= r.targetAdd*0.95 ? "good" : r.realAdd >= r.targetAdd*0.7 ? "ok" : "bad"}`}>
                {Math.round((r.realAdd / r.targetAdd) * 100)}%
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Churn € / % */}
      <td className="num">
        <div className="cell-stack">
          <div className="cell-row v-primary">
            <EditableNumber value={Math.round(r.churnAbs)} onChange={(v) => setMonth(r.key, {churnAbs: v})} prefix="€" />
          </div>
          <div className="cell-row v-secondary">
            <span>{r.churnPct.toFixed(1)}%</span>
          </div>
        </div>
      </td>

      <td className="mix compact-hide"><MixBar row={r} /></td>

      {/* GTM cell: target on top, Real on bottom. Real = gtmTotal which now
          includes team-owned upsells per Marco's 2026-05 directive.
          Display rule (Marco's UX refinement): show a SINGLE total value;
          the sales-vs-upsell split lives in the hover tooltip + the ⓘ
          inspector breakdown. No more "+€X ups." inline chip. */}
      <td className="num compact-hide">
        {(() => {
          const gtmDeals = (dealsByCell?.gtmActual) || [];
          const teamUpsellDeals = (dealsByCell?.upsellPipeline) || [];
          const combinedGtmDeals = [...gtmDeals, ...teamUpsellDeals];
          const teamUpsellSum = teamUpsellDeals.reduce((s, d) => s + (d.amount || 0), 0);
          const gtmSum = gtmDeals.reduce((s, d) => s + (d.amount || 0), 0);
          // Displayed total prefers the model's gtmTotal (gtmActual + team
          // upsell). When the model has no actual yet AND deals exist (rare:
          // hydrate hasn't fired), fall back to the deal-sum so the cell
          // still surfaces meaningful data.
          const displayed = (typeof r.gtmTotal === "number")
            ? r.gtmTotal
            : ((gtmSum + teamUpsellSum) > 0 ? (gtmSum + teamUpsellSum) : null);
          // Tooltip content: sales + upsell breakdown rendered as a tiny
          // table. Only built when team upsell > 0 (otherwise the display
          // value IS sales — nothing to break down).
          const tipNode = teamUpsellSum > 0 ? (
            <div className="gtm-tip">
              <div className="gtm-tip__row">
                <span className="gtm-tip__label">{lang === "it" ? "Vendite GTM" : "GTM sales"}</span>
                <span className="gtm-tip__amount">{eur(r.gtmActual ?? 0)}</span>
              </div>
              <div className="gtm-tip__row">
                <span className="gtm-tip__label">{lang === "it" ? "Upsell team" : "Team Upsell"}</span>
                <span className="gtm-tip__amount">+{eur(teamUpsellSum)}</span>
              </div>
              <div className="gtm-tip__row gtm-tip__row--total">
                <span className="gtm-tip__label">{lang === "it" ? "Totale" : "Total"}</span>
                <span className="gtm-tip__amount">{eur(displayed ?? 0)}</span>
              </div>
            </div>
          ) : null;
          return (
            <div className="cell-stack">
              <div className="cell-row v-primary">
                <EditableNumber value={Math.round(r.gtmTarget)} onChange={(v) => setMonth(r.key, {gtmTarget: v})} prefix="€" />
              </div>
              <div className="cell-row v-secondary">
                <span className="v-label">R</span>
                {/* When there's no team upsell, keep the editable input so
                    the user can still type a value (legacy behavior intact
                    for future months / non-HubSpot scenarios). When team
                    upsell exists, switch to a read-only Tooltip-wrapped
                    span showing just the total. */}
                {teamUpsellSum > 0 ? (
                  <Tooltip tip={tipNode}>
                    <span className="v-actual gtm-actual-total">
                      {typeof displayed === "number" ? eur(displayed) : "—"}
                    </span>
                  </Tooltip>
                ) : (
                  <EditableNumber value={r.gtmActual} onChange={(v) => setMonth(r.key, {gtmActual: v})} prefix="€" allowEmpty={true} placeholder="—" />
                )}
                <DealInspector
                  deals={combinedGtmDeals}
                  portalId={portalId}
                  ownersMap={ownersMap}
                  lang={lang}
                  fmtMoney={eur}
                  cellLabel={lang === "it" ? "GTM (incl. Upsell team)" : "GTM (incl. team Upsell)"}
                  total={typeof displayed === "number" ? displayed : (gtmSum + teamUpsellSum)}
                  breakdown={teamUpsellSum > 0 ? [
                    { label: lang === "it" ? "Vendite GTM (HubSpot)" : "GTM sales (HubSpot)", amount: gtmSum,        dealCount: gtmDeals.length },
                    { label: lang === "it" ? "Upsell team (HubSpot)" : "Team Upsell (HubSpot)", amount: teamUpsellSum, dealCount: teamUpsellDeals.length },
                  ] : undefined}
                />
                {typeof displayed === "number" && r.gtmTarget > 0 && (
                  <span className={`perf-mini ${displayed >= r.gtmTarget*0.95 ? "good" : displayed >= r.gtmTarget*0.7 ? "ok" : "bad"}`}>
                    {Math.round((displayed / r.gtmTarget) * 100)}%
                  </span>
                )}
              </div>
            </div>
          );
        })()}
      </td>

      {/* Partner */}
      <td className="num compact-hide">
        <BoundChannelCell
          eur={r.partnerMrr} pct={r.partnerPct}
          actual={r.partnerActual}
          onEur={(v) => setMonth(r.key, {partnerMrr: v})}
          onActual={(v) => setMonth(r.key, {partnerActual: v})}
          lang={lang}
          deals={(dealsByCell?.partnerActual) || []}
          portalId={portalId}
          ownersMap={ownersMap}
          fmtMoney={eur}
          cellLabel="Partner"
        />
      </td>

      {/* Self-Serve */}
      <td className="num compact-hide">
        <BoundChannelCell
          eur={r.selfServeMrr} pct={r.selfServePct}
          actual={r.selfServeActual}
          onEur={(v) => setMonth(r.key, {selfServeMrr: v})}
          onActual={(v) => setMonth(r.key, {selfServeActual: v})}
          lang={lang}
          deals={(dealsByCell?.selfServeActual) || []}
          portalId={portalId}
          ownersMap={ownersMap}
          fmtMoney={eur}
          cellLabel="Self-Serve"
        />
      </td>

      {/* Others column — 2-row cell. See OthersCell component for layout.
          2026-05: team Upsell rolled into GTM; only non-team upsells appear
          here alongside Setup and Other AEs (non-team GTM deals). */}
      <td className="num compact-hide">
        {(() => {
          const setupFcst       = Math.round(m.setupUpsell || 0);
          const upsellFcst      = Math.round(m.upsellPipeline || 0);
          const setupReal       = Math.round(m.setupUpsellActual || 0);
          const otherReal       = Math.round(m.otherActual || 0);
          const otherUpsellReal = Math.round(m.otherUpsellActual || 0);
          const otherUpsellList = (dealsByCell?.otherUpsellActual) || [];
          const list = otherDeals || [];
          const lblF = lang === "it" ? "Forecast" : "Forecast";
          const lblR = lang === "it" ? "Reale (HubSpot)" : "Real (HubSpot)";
          const tipForecast = `${lblF}:\n  Setup: ${eur(setupFcst)}\n  Upsell: ${eur(upsellFcst)}`;
          const tipReal = `${lblR}:\n  Setup: ${eur(setupReal)}\n  Altri AE: ${eur(otherReal)}\n  Altri Upsell: ${eur(otherUpsellReal)}`;
          const otherDetails = list.length > 0
            ? "\n" + list.map(d => {
                const owner = ownersMap?.[d.ownerId]?.name || `id ${d.ownerId || "—"}`;
                return `  · ${owner} → ${d.name} (${eur(d.amount)})`;
              }).join("\n")
            : "";
          const tip = `${tipForecast}\n\n${tipReal}${otherDetails}`;
          return (
            <OthersCell
              row={r} m={m}
              setupFcst={setupFcst} upsellFcst={upsellFcst}
              setupReal={setupReal} otherUpsellReal={otherUpsellReal} otherReal={otherReal}
              otherDeals={list}
              setupDeals={(dealsByCell?.setupUpsell) || []}
              otherUpsellDeals={otherUpsellList}
              portalId={portalId}
              ownersMap={ownersMap}
              eur={eur} setMonth={setMonth} lang={lang}
              othersTotal={r.othersTotal} tip={tip}
            />
          );
        })()}
      </td>

      {/* AE channel: residual € to hit target add, with hired-vs-needed below */}
      <td className="num input-col">
        <div className="cell-stack">
          <div className="cell-row v-primary">
            <span>{eur(r.aeChannelGap)}</span>
            <span className="v-secondary" style={{fontSize: "var(--fs-caption)"}}>{r.aePct.toFixed(0)}%</span>
          </div>
          <div className="cell-row v-secondary">
            <span className={`ae-need-pill ${aeGap > 0 ? "ae-need-pill--gap" : "ae-need-pill--ok"}`}>
              <span className="ae-need-pill__num"><b>{aeHired}</b>/<b>{Math.round(aeNeeded)}</b></span>
              {aeGap > 0
                ? <span className="ae-need-label">+{aeGap} {lang === "it" ? "da assumere" : "to hire"}</span>
                : <span className="ae-need-label">{lang === "it" ? "team ok" : "team ok"}</span>}
            </span>
          </div>
        </div>
      </td>

      {/* Per-AE columns: small "target" caption + just the real € input */}
      {r.aes.map(ae => {
        const mAE = (m.aes || {})[ae.id] || {};
        const target = ae.target;       // global target * seasonality
        const real = (typeof mAE.real === "number") ? mAE.real : null;
        return (
          <td key={ae.id} className="num ae-col">
            <div className="cell-stack">
              <div className="cell-row ae-target-mini">
                <EditableNumber value={target} onChange={(v) => setAeMonth(r.key, ae.id, {target: v})} prefix="€" kind="ae" monthKey={r.key} />
                <span className="ae-target-mini__lbl">{lang === "it" ? "target" : "target"}</span>
              </div>
              <div className="cell-row v-primary">
                <EditableNumber value={real} onChange={(v) => setAeMonth(r.key, ae.id, {real: v})} prefix="€" allowEmpty={true} placeholder="—" kind="ae" monthKey={r.key} />
                {typeof target === "number" && target > 0 && typeof real === "number" && (
                  <span className={`perf-mini ${real >= target*0.95 ? "good" : real >= target*0.7 ? "ok" : "bad"}`}>
                    {Math.round((real / target) * 100)}%
                  </span>
                )}
              </div>
            </div>
          </td>
        );
      })}

      {/* SDR */}
      <td className="num team">
        <div className="cell-stack">
          <div className="cell-row v-primary">
            <span>{(Math.round(r.sdrNeeded*10)/10)}</span>
          </div>
          <div className="cell-row v-secondary">
            <span>{Math.round(r.callsNeeded).toLocaleString("de-DE")} {lang === "it" ? "call" : "calls"}</span>
          </div>
        </div>
      </td>
    </tr>
  );
}
// Phase D: memoize MonthRow. With slice props (monthData / lang / aes /
// tableShowMoM) and useCallback'd setters (from ForecastTable), the shallow
// compare succeeds for rows whose specific slice didn't change. Per-keystroke
// re-render cost drops from O(12 rows × ~30 cells) to O(1 row × ~30 cells)
// — typically ~12× faster for cell edits, the dashboard's hot path.
const MonthRowMemo = React.memo(MonthRow);

// ─── ForecastTable — top-level wrapper ─────────────────────────────────────
// Renders the entire Card section (header + body). Branches on timeframe.mode
// between MonthDetailCard grid (month/qtr/custom) and the full <table> layout
// (fy/ytd). Internal setters (setMonth, setAeMonth, setAeField, setAeCount,
// setTop) are derived from setState; this keeps the prop surface narrow and
// memo-friendly for Phase D.
function ForecastTable({
  state, setState, t, lang, rows, visibleRows, today, actuals, timeframe,
  mrrFmt, tableEur,
  tableValueMode, setTableValueMode,
  tableExpanded, setTableExpanded,
  sectionOpen, onToggleSection,
}) {
  const Card = window.Card;
  const SectionHeader = window.SectionHeader;
  const Button = window.Button;

  // Internal setters — derive from setState so the prop surface stays narrow.
  // Phase D: wrapped in useCallback so the references stay stable across renders.
  // Without this, MonthRow / MonthDetailCard's React.memo would re-render on
  // every parent render because setMonth/setAeMonth refs would change.
  // setState itself is stable (React useState contract), so the dep is empty.
  const setTop = React.useCallback(
    (patch) => setState(s => ({ ...s, ...patch })),
    [setState]
  );
  const setMonth = React.useCallback(
    (k, patch) => setState(s => ({ ...s, months: { ...s.months, [k]: { ...s.months[k], ...patch } } })),
    [setState]
  );
  const setAeField = React.useCallback(
    (id, patch) => setState(s => ({ ...s, aes: (s.aes || []).map(a => a.id === id ? { ...a, ...patch } : a) })),
    [setState]
  );
  const setAeMonth = React.useCallback(
    (mKey, aeId, patch) => setState(s => {
      const months = { ...s.months };
      const m = { ...(months[mKey] || {}) };
      m.aes = { ...(m.aes || {}) };
      m.aes[aeId] = { ...(m.aes[aeId] || {}), ...patch };
      months[mKey] = m;
      return { ...s, months };
    }),
    [setState]
  );
  const setAeCount = React.useCallback(
    (n) => {
      setState(s => {
        const current = (s.aes || []).length;
        if (n === current) return s;
        if (n > current) {
          const newAes = [...(s.aes || [])];
          for (let i = current; i < n; i++) newAes.push({ id: `ae${Date.now()}_${i}`, name: `AE ${i + 1}`, person: "" });
          return { ...s, aes: newAes, aeCount: n };
        }
        return { ...s, aes: (s.aes || []).slice(0, n), aeCount: n };
      });
    },
    [setState]
  );

  return (
    <Card padding="none" elevation="flat" collapsed={!sectionOpen}
      className={`card ${tableExpanded ? "table-card--expanded" : "table-card--compact"} ${sectionOpen ? "" : "card--collapsed"}`}>
      <SectionHeader
        title={
          timeframe.mode === "month" ? (lang === "it" ? "Dettaglio mese" : "Month detail")
            : timeframe.mode === "qtr" ? (lang === "it" ? "Dettaglio trimestre" : "Quarter detail")
            : t.sectionTimeline
        }
        open={sectionOpen}
        onToggle={onToggleSection}
      >
        <div className="ae-control">
          <span className="tr-legend">
            <span className="v-label">R</span> = {lang === "it" ? "reale" : "actual"}
          </span>
          {/* Per-table MRR/ARR toggle (independent of global) */}
          <div className="seg-toggle seg-toggle--sm" title={lang === "it" ? "Mostra valori in MRR o ARR (solo questa tabella)" : "Show values in MRR or ARR (this table only)"} style={{marginRight: 8}}>
            <button className={`seg-toggle__btn ${tableValueMode === "mrr" ? "on" : ""}`} onClick={(e) => {e.stopPropagation(); setTableValueMode("mrr");}}>MRR</button>
            <button className={`seg-toggle__btn ${tableValueMode === "arr" ? "on" : ""}`} onClick={(e) => {e.stopPropagation(); setTableValueMode("arr");}}>ARR</button>
          </div>
          {/* MoM toggle — when ON, every numeric cell shows MoM % beside its value */}
          <button className={`btn-toggle ${state.tableShowMoM ? "on" : ""}`}
            onClick={(e) => {e.stopPropagation(); setTop({tableShowMoM: !state.tableShowMoM});}}
            title={lang === "it" ? "Mostra/nascondi delta % vs mese precedente in ogni cella" : "Show/hide MoM % delta in each cell"}
            style={{marginRight: 8}}>
            %MoM
          </button>
          <label onClick={(e) => e.stopPropagation()}>{t.aeCountLabel}:</label>
          <button className="ae-btn" onClick={(e) => {e.stopPropagation(); setAeCount((state.aeCount || 0) - 1);}} disabled={(state.aeCount || 0) <= 0}>−</button>
          <span className="ae-count">{state.aeCount || 0}</span>
          <button className="ae-btn" onClick={(e) => {e.stopPropagation(); setAeCount((state.aeCount || 0) + 1);}}>+</button>
          {(timeframe.mode !== "month" && timeframe.mode !== "qtr") && (
            <Button variant="ghost" size="sm"
              onClick={(e) => {e.stopPropagation(); setTableExpanded(ex => !ex);}}
              title={tableExpanded ? (lang === "it" ? "Comprimi" : "Collapse") : (lang === "it" ? "Espandi" : "Expand")}
              style={{marginLeft: 8}}
            >
              {tableExpanded ? "⤡" : "⤢"} {tableExpanded ? (lang === "it" ? "Comprimi" : "Collapse") : (lang === "it" ? "Espandi" : "Expand")}
            </Button>
          )}
        </div>
      </SectionHeader>
      {sectionOpen && (
      <>
      {(timeframe.mode === "month" || timeframe.mode === "qtr") ? (
        <div className={`mdc-grid ${timeframe.mode === "qtr" ? "mdc-grid--qtr" : "mdc-grid--single"}`}>
          {visibleRows.map(r => (
            <MonthDetailCardMemo
              key={r.key} row={r}
              monthData={state.months[r.key]}
              aes={state.aes}
              setMonth={setMonth} setAeMonth={setAeMonth}
              lang={lang}
              isCurrent={r.key === today.monthKey}
              mrrFmt={mrrFmt} tableEur={tableEur}
            />
          ))}
        </div>
      ) : (
        <div className="table-wrap">
        <table className="forecast">
          <thead>
            <tr>
              <th>{t.colMonth}</th>
              <th className="num compact-hide">{t.colSeas}</th>
              <th className="num"><div className="th-stack"><span>{t.colEomTarget.replace("MRR", tableValueMode === "arr" ? "ARR" : "MRR")}</span><span className="th-sub">{t.colEomReal.replace("MRR", tableValueMode === "arr" ? "ARR" : "MRR")}</span></div></th>
              <th className="num"><div className="th-stack"><span>{t.colTargetAdd.replace("MRR", tableValueMode === "arr" ? "ARR" : "MRR")}</span><span className="th-sub">{t.colRealAdd.replace("MRR", tableValueMode === "arr" ? "ARR" : "MRR")}</span></div></th>
              <th className="num"><div className="th-stack"><span>{t.colChurnEur}</span><span className="th-sub">{t.colChurnPct}</span></div></th>
              <th className="mix compact-hide">{t.colMix}</th>
              <th className="num compact-hide">{t.colGTM}</th>
              <th className="num compact-hide">{t.colPartner}</th>
              <th className="num compact-hide">{t.colSelf}</th>
              <th className="num compact-hide" title={t.othersTooltip}>{t.colOthers} <span className="info-i">ⓘ</span></th>
              <th className="num input-col">{t.colAE}</th>
              {(state.aes || []).map(ae => (
                <th key={ae.id} className="num ae-col">
                  <div className="ae-th-stack">
                    <EditableText value={ae.name} onChange={(v) => setAeField(ae.id, {name: v})} className="ae-th-name" kind="ae" />
                    <EditableText value={ae.person || ""} onChange={(v) => setAeField(ae.id, {person: v})} className="ae-th-person" kind="ae" />
                  </div>
                </th>
              ))}
              <th className="num team" title={t.sdrTooltip}>
                {t.colSDR}
                <span className="info-i">ⓘ</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Pre-bucket deals by (monthKey, cell) so each MonthRow can render
              // its tooltip + DealInspector popovers without re-scanning the full
              // deals list. Cells we care about for the inspector:
              //   gtmActual, partnerActual, selfServeActual, setupUpsell,
              //   upsellPipeline, otherActual (also otherCommitted for tooltip).
              // dealsByMonth[monthKey] = { [cell]: Deal[] }.
              const dealsByMonth = {};
              for (const d of (actuals?.deals || [])) {
                if (!d.monthKey || !d.cell) continue;
                if (!dealsByMonth[d.monthKey]) dealsByMonth[d.monthKey] = {};
                const bucket = dealsByMonth[d.monthKey];
                if (!bucket[d.cell]) bucket[d.cell] = [];
                bucket[d.cell].push(d);
              }
              return visibleRows.map((r) => {
                const monthBuckets = dealsByMonth[r.key] || {};
                // Real Add = GTM (with team Upsell rolled in) + Partner + Self-Serve
                // + Other AEs + Other Upsell. Setup excluded (per model.ts).
                // 2026-05 reshuffle: upsellPipeline = team-only now; non-team
                // upsells live in otherUpsellActual.
                const realAddDeals = [
                  ...(monthBuckets.gtmActual || []),
                  ...(monthBuckets.upsellPipeline || []),       // team Upsell → with GTM
                  ...(monthBuckets.partnerActual || []),
                  ...(monthBuckets.selfServeActual || []),
                  ...(monthBuckets.otherActual || []),
                  ...(monthBuckets.otherUpsellActual || []),    // non-team Upsell → in Others
                ];
                const otherDeals = [
                  ...(monthBuckets.otherActual || []),
                  ...(monthBuckets.otherCommitted || []),
                ];
                return (
                  <MonthRowMemo
                    key={r.key} r={r}
                    monthData={state.months[r.key]}
                    lang={lang}
                    aes={state.aes}
                    tableShowMoM={state.tableShowMoM}
                    setMonth={setMonth} setAeMonth={setAeMonth}
                    t={t}
                    isCurrent={r.key === today.monthKey}
                    mrrFmt={mrrFmt} tableEur={tableEur}
                    otherDeals={otherDeals}
                    dealsByCell={monthBuckets}
                    realAddDeals={realAddDeals}
                    portalId={actuals?.portalId || null}
                    ownersMap={actuals?.owners || {}}
                    prevRow={(() => {
                      const idx = rows.findIndex(x => x.key === r.key);
                      return idx > 0 ? rows[idx - 1] : null;
                    })()}
                    prevExtraHires={(() => {
                      const idx = rows.findIndex(x => x.key === r.key);
                      return idx > 0 ? (rows[idx - 1].extraHiresNeeded || 0) : 0;
                    })()}
                  />
                );
              });
            })()}
          </tbody>
        </table>
      </div>
      )}
      </>
      )}
    </Card>
  );
}

// ─── ESM exports (Phase 4 sub-step 4) ──────────────────────────────────────
// Memoized variants are the public API — external callers see no difference,
// React handles memoization transparently as long as props are stable.
export default ForecastTable;
export {
  MonthRowMemo as MonthRow,
  MonthDetailCardMemo as MonthDetailCard,
  OthersCell,
  BoundChannelCell,
  MixBar,
  MomBadge,
  EditableText,
};
